import React, { useEffect, useMemo, useState } from 'react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import statisticsService, {
  StatisticsData as BackendStatisticsData,
  ProductStatistics,
  ClientStatistics,
  SupplierStatistics,
  StatisticsFilters,
} from '@/services/statisticsService';
import SearchableSelect from '@/components/SearchableSelect';
import optionsService from '@/services/optionsService';
import productsService from '@/services/productsService';

interface ChartData {
  date: string;
  total: number;
  completed: number;
  rejected: number;
  inProgress: number;
}

interface PeriodOption {
  value: 'day' | 'week' | 'month' | 'year' | 'custom';
  label: string;
  getDateRange?: () => { start: Date; end: Date };
}

const COLORS = ['#3B82F6', '#10B981', '#EF4444', '#F59E0B'];

type ChartId = 'timeline' | 'status' | 'stacked' | 'pie';

const CHART_TITLES: Record<ChartId, string> = {
  timeline: 'Évolution temporelle',
  status: 'Répartition par statut',
  stacked: 'Volume empilé',
  pie: 'Répartition globale',
};

const formatKg = (value?: number | null) =>
  `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} kg`;

const StatisticsTab: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month' | 'year' | 'custom'>('month');
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  });
  const emptyExtraFilters = {
    type_pesee: '',
    status: '',
    product_id: '',
    client_id: '',
    fournisseur_id: '',
    magasin_id: '',
  };
  const [appliedFilters, setAppliedFilters] = useState(emptyExtraFilters);
  const [draftPeriod, setDraftPeriod] = useState(selectedPeriod);
  const [draftDateRange, setDraftDateRange] = useState(dateRange);
  const [draftFilters, setDraftFilters] = useState(emptyExtraFilters);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [expandedChart, setExpandedChart] = useState<ChartId | null>(null);
  const [statisticsData, setStatisticsData] = useState<ChartData[]>([]);
  const [generalStats, setGeneralStats] = useState<BackendStatisticsData | null>(null);
  const [productStats, setProductStats] = useState<ProductStatistics[]>([]);
  const [clientStats, setClientStats] = useState<ClientStatistics[]>([]);
  const [supplierStats, setSupplierStats] = useState<SupplierStatistics[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [productOptions, setProductOptions] = useState<{ value: string; label: string }[]>([]);
  const [clientOptions, setClientOptions] = useState<{ value: string; label: string }[]>([]);
  const [fournisseurOptions, setFournisseurOptions] = useState<{ value: string; label: string }[]>([]);
  const [magasinOptions, setMagasinOptions] = useState<{ value: string; label: string }[]>([]);

  const periodOptions: PeriodOption[] = [
    {
      value: 'day',
      label: 'Aujourd\'hui',
      getDateRange: () => ({ start: startOfDay(new Date()), end: endOfDay(new Date()) })
    },
    {
      value: 'week',
      label: 'Cette semaine',
      getDateRange: () => ({
        start: startOfWeek(new Date(), { locale: fr }),
        end: endOfWeek(new Date(), { locale: fr })
      })
    },
    {
      value: 'month',
      label: 'Ce mois',
      getDateRange: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) })
    },
    {
      value: 'year',
      label: 'Cette année',
      getDateRange: () => ({ start: startOfYear(new Date()), end: endOfYear(new Date()) })
    },
    { value: 'custom', label: 'Personnalisée' }
  ];

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [products, options] = await Promise.all([
          productsService.getProducts(),
          optionsService.getAllOptions(),
        ]);
        setProductOptions(products);
        setClientOptions(options.clients || []);
        setFournisseurOptions(options.fournisseurs || []);
        setMagasinOptions(options.magasins || []);
      } catch (err) {
        console.error('Erreur chargement options stats:', err);
      }
    };
    loadOptions();
  }, []);

  useEffect(() => {
    loadStatisticsData();
  }, [selectedPeriod, dateRange, appliedFilters]);

  const buildApiFilters = (): StatisticsFilters => ({
    start_date: format(dateRange.start, 'yyyy-MM-dd'),
    end_date: format(dateRange.end, 'yyyy-MM-dd'),
    period: selectedPeriod === 'custom' ? 'month' : selectedPeriod,
    type_pesee: appliedFilters.type_pesee || undefined,
    status: appliedFilters.status || undefined,
    product_id: appliedFilters.product_id || undefined,
    client_id: appliedFilters.client_id || undefined,
    fournisseur_id: appliedFilters.fournisseur_id || undefined,
    magasin_id: appliedFilters.magasin_id || undefined,
  });

  const loadStatisticsData = async () => {
    setLoading(true);
    setError('');
    const filters = buildApiFilters();

    try {
      const [generalStatsData, timeSeriesData, products, clients, suppliers] = await Promise.all([
        statisticsService.getGeneralStats(filters),
        statisticsService.getTimeSeriesData(filters),
        statisticsService.getProductStats(filters).catch(() => []),
        statisticsService.getClientStats(filters).catch(() => []),
        statisticsService.getSupplierStats(filters).catch(() => []),
      ]);

      setGeneralStats(generalStatsData);
      setProductStats((products || []).slice(0, 8));
      setClientStats((clients || []).slice(0, 8));
      setSupplierStats((suppliers || []).slice(0, 8));

      const chartData: ChartData[] = (timeSeriesData || []).map((item) => ({
        date: format(new Date(item.date), 'dd/MM'),
        total: item.total || 0,
        completed: item.completed || 0,
        rejected: item.rejected || 0,
        inProgress: item.in_progress || 0,
      }));
      setStatisticsData(chartData);
    } catch (err: any) {
      console.error('Erreur statistiques:', err);
      setError(err?.message || 'Erreur lors du chargement des statistiques');
      setStatisticsData([]);
      setGeneralStats(null);
    } finally {
      setLoading(false);
    }
  };

  const openFiltersModal = () => {
    setDraftPeriod(selectedPeriod);
    setDraftDateRange(dateRange);
    setDraftFilters({ ...appliedFilters });
    setShowFiltersModal(true);
  };

  const handleDraftPeriodChange = (period: PeriodOption['value']) => {
    setDraftPeriod(period);
    const option = periodOptions.find((opt) => opt.value === period);
    if (option?.getDateRange) {
      setDraftDateRange(option.getDateRange());
    }
  };

  const applyFilters = () => {
    setSelectedPeriod(draftPeriod);
    setDateRange(draftDateRange);
    setAppliedFilters({ ...draftFilters });
    setShowFiltersModal(false);
  };

  const resetFilters = () => {
    const monthRange = { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
    setDraftPeriod('month');
    setDraftDateRange(monthRange);
    setDraftFilters({ ...emptyExtraFilters });
    setSelectedPeriod('month');
    setDateRange(monthRange);
    setAppliedFilters({ ...emptyExtraFilters });
    setShowFiltersModal(false);
  };

  const completionRate = useMemo(() => {
    const total = generalStats?.total_pesées || 0;
    if (!total) return 0;
    return Math.round(((generalStats?.pesées_complétées || 0) / total) * 1000) / 10;
  }, [generalStats]);

  const rejectionRate = useMemo(() => {
    const total = generalStats?.total_pesées || 0;
    if (!total) return 0;
    return Math.round(((generalStats?.pesées_refoulées || 0) / total) * 1000) / 10;
  }, [generalStats]);

  const activeFilterLabels = useMemo(() => {
    const labels: string[] = [];
    if (selectedPeriod !== 'month') {
      const periodLabel = periodOptions.find((p) => p.value === selectedPeriod)?.label;
      if (periodLabel) labels.push(`Période: ${periodLabel}`);
    }
    if (appliedFilters.type_pesee) labels.push(`Type: ${appliedFilters.type_pesee}`);
    if (appliedFilters.status) labels.push(`Statut: ${appliedFilters.status}`);
    if (appliedFilters.product_id) labels.push('Produit');
    if (appliedFilters.client_id) labels.push('Client');
    if (appliedFilters.fournisseur_id) labels.push('Fournisseur');
    if (appliedFilters.magasin_id) labels.push('Magasin');
    return labels;
  }, [appliedFilters, selectedPeriod]);

  const activeFiltersCount = useMemo(() => {
    return [
      appliedFilters.type_pesee,
      appliedFilters.status,
      appliedFilters.product_id,
      appliedFilters.client_id,
      appliedFilters.fournisseur_id,
      appliedFilters.magasin_id,
      selectedPeriod !== 'month' ? 'period' : '',
    ].filter(Boolean).length;
  }, [appliedFilters, selectedPeriod]);

  const exportPdfReport = async () => {
    if (!generalStats) return;
    setExporting(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const periodLabel = `${format(dateRange.start, 'dd/MM/yyyy')} - ${format(dateRange.end, 'dd/MM/yyyy')}`;

      doc.setFontSize(16);
      doc.text('Rapport de statistiques - Pont Bascule', 14, 18);
      doc.setFontSize(10);
      doc.setTextColor(80);
      doc.text(`Période: ${periodLabel}`, 14, 26);
      doc.text(`Généré le: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 32);
      if (activeFilterLabels.length) {
        doc.text(`Filtres: ${activeFilterLabels.join(' · ')}`, 14, 38);
      }

      autoTable(doc, {
        startY: activeFilterLabels.length ? 44 : 40,
        head: [['Indicateur', 'Valeur']],
        body: [
          ['Total pesées', String(generalStats.total_pesées || 0)],
          ['Complétées', String(generalStats.pesées_complétées || 0)],
          ['Refoulées', String(generalStats.pesées_refoulées || 0)],
          ['En cours', String(generalStats.pesées_en_cours || 0)],
          ['En attente tare', String(generalStats.pesées_en_attente || 0)],
          ['Taux de complétion', `${completionRate} %`],
          ['Taux de refoulement', `${rejectionRate} %`],
          ['Poids brut total', formatKg(generalStats.poids_total_brut)],
          ['Poids net total', formatKg(generalStats.poids_total_net)],
          ['Poids brut moyen', formatKg(generalStats.poids_moyen_brut)],
          ['Poids net moyen', formatKg(generalStats.poids_moyen_net)],
        ],
        styles: { fontSize: 9 },
        headStyles: { fillColor: [37, 99, 235] },
      });

      let nextY = (doc as any).lastAutoTable?.finalY + 10 || 120;

      if (productStats.length) {
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text('Top produits', 14, nextY);
        autoTable(doc, {
          startY: nextY + 4,
          head: [['Produit', 'Pesées', 'Poids total', 'Taux complétion']],
          body: productStats.map((p) => [
            p.produit_nom || 'N/A',
            String(p.total_pesées || 0),
            formatKg(Number(p.poids_total || 0)),
            `${p.taux_complétion || 0} %`,
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [16, 185, 129] },
        });
        nextY = (doc as any).lastAutoTable?.finalY + 10 || nextY + 40;
      }

      if (clientStats.length) {
        if (nextY > 250) {
          doc.addPage();
          nextY = 20;
        }
        doc.setFontSize(12);
        doc.text('Top clients', 14, nextY);
        autoTable(doc, {
          startY: nextY + 4,
          head: [['Client', 'Pesées', 'Poids total', 'Taux complétion']],
          body: clientStats.map((c) => [
            c.client_nom || 'N/A',
            String(c.total_pesées || 0),
            formatKg(Number(c.poids_total || 0)),
            `${c.taux_complétion || 0} %`,
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [245, 158, 11] },
        });
        nextY = (doc as any).lastAutoTable?.finalY + 10 || nextY + 40;
      }

      if (supplierStats.length) {
        if (nextY > 250) {
          doc.addPage();
          nextY = 20;
        }
        doc.setFontSize(12);
        doc.text('Top fournisseurs', 14, nextY);
        autoTable(doc, {
          startY: nextY + 4,
          head: [['Fournisseur', 'Pesées', 'Poids total', 'Taux complétion']],
          body: supplierStats.map((s) => [
            s.fournisseur_nom || 'N/A',
            String(s.total_pesées || 0),
            formatKg(Number(s.poids_total || 0)),
            `${s.taux_complétion || 0} %`,
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [99, 102, 241] },
        });
      }

      const fileName = `rapport_statistiques_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`;
      doc.save(fileName);
    } catch (err: any) {
      console.error('Erreur export PDF:', err);
      alert(err?.message || 'Impossible de générer le PDF');
    } finally {
      setExporting(false);
    }
  };

  const kpiCards = [
    {
      label: 'Total pesées',
      value: (generalStats?.total_pesées || 0).toLocaleString('fr-FR'),
      icon: 'fas fa-chart-line',
      color: 'bg-blue-50 text-blue-700 border-blue-200',
      iconBg: 'bg-blue-100 text-blue-600',
    },
    {
      label: 'Complétées',
      value: (generalStats?.pesées_complétées || 0).toLocaleString('fr-FR'),
      icon: 'fas fa-check-circle',
      color: 'bg-green-50 text-green-700 border-green-200',
      iconBg: 'bg-green-100 text-green-600',
    },
    {
      label: 'Refoulées',
      value: (generalStats?.pesées_refoulées || 0).toLocaleString('fr-FR'),
      icon: 'fas fa-ban',
      color: 'bg-red-50 text-red-700 border-red-200',
      iconBg: 'bg-red-100 text-red-600',
    },
    {
      label: 'En cours',
      value: (generalStats?.pesées_en_cours || 0).toLocaleString('fr-FR'),
      icon: 'fas fa-spinner',
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      iconBg: 'bg-amber-100 text-amber-600',
    },
    {
      label: 'Poids net total',
      value: formatKg(generalStats?.poids_total_net),
      icon: 'fas fa-weight-hanging',
      color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      iconBg: 'bg-indigo-100 text-indigo-600',
    },
    {
      label: 'Poids brut total',
      value: formatKg(generalStats?.poids_total_brut),
      icon: 'fas fa-balance-scale',
      color: 'bg-slate-50 text-slate-700 border-slate-200',
      iconBg: 'bg-slate-100 text-slate-600',
    },
    {
      label: 'Poids net moyen',
      value: formatKg(generalStats?.poids_moyen_net),
      icon: 'fas fa-calculator',
      color: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      iconBg: 'bg-cyan-100 text-cyan-600',
    },
    {
      label: 'Taux complétion',
      value: `${completionRate} %`,
      icon: 'fas fa-percentage',
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      iconBg: 'bg-emerald-100 text-emerald-600',
    },
  ];

  const pieData = [
    { name: 'Complétées', value: generalStats?.pesées_complétées || 0 },
    { name: 'Refoulées', value: generalStats?.pesées_refoulées || 0 },
    { name: 'En cours', value: generalStats?.pesées_en_cours || 0 },
  ];

  const renderChart = (chartId: ChartId, height: number) => {
    switch (chartId) {
      case 'timeline':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={statisticsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2} name="Total" />
              <Line type="monotone" dataKey="completed" stroke="#10B981" strokeWidth={2} name="Complétées" />
              <Line type="monotone" dataKey="rejected" stroke="#EF4444" strokeWidth={2} name="Refoulées" />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'status':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={statisticsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="completed" fill="#10B981" name="Complétées" />
              <Bar dataKey="rejected" fill="#EF4444" name="Refoulées" />
              <Bar dataKey="inProgress" fill="#F59E0B" name="En cours" />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'stacked':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={statisticsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="completed" stackId="1" stroke="#10B981" fill="#10B981" name="Complétées" />
              <Area type="monotone" dataKey="rejected" stackId="1" stroke="#EF4444" fill="#EF4444" name="Refoulées" />
              <Area type="monotone" dataKey="inProgress" stackId="1" stroke="#F59E0B" fill="#F59E0B" name="En cours" />
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={height > 400 ? 160 : 80}
                dataKey="value"
              >
                {pieData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  const chartIds: ChartId[] = ['timeline', 'status', 'stacked', 'pie'];
  const expandedChartHeight =
    typeof window !== 'undefined' ? Math.min(560, Math.round(window.innerHeight * 0.65)) : 560;

  return (
    <div className="p-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Statistiques des Pesées</h2>
          <p className="text-sm text-gray-600 mt-1">
            Période: {format(dateRange.start, 'dd/MM/yyyy', { locale: fr })} -{' '}
            {format(dateRange.end, 'dd/MM/yyyy', { locale: fr })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openFiltersModal}
            className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-lg text-sm transition-colors flex items-center"
          >
            <i className="fas fa-filter mr-2 text-blue-600" />
            Filtres
            {activeFiltersCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold">
                {activeFiltersCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={exportPdfReport}
            disabled={exporting || loading || !generalStats}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm transition-colors flex items-center"
          >
            {exporting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            ) : (
              <i className="fas fa-file-pdf mr-2" />
            )}
            Exporter PDF
          </button>
        </div>
      </div>

      {activeFilterLabels.length > 0 && (
        <div className="flex flex-wrap gap-2 -mt-3 mb-6">
          {activeFilterLabels.map((label) => (
            <span key={label} className="px-2.5 py-1 bg-blue-50 text-blue-800 border border-blue-100 rounded-full text-xs">
              {label}
            </span>
          ))}
        </div>
      )}

      {showFiltersModal && (
        <div className="fixed inset-0 z-50 flex">
          <button
            type="button"
            aria-label="Fermer les filtres"
            className="flex-1 bg-black/40"
            onClick={() => setShowFiltersModal(false)}
          />
          <aside className="w-full max-w-md h-full bg-white shadow-2xl flex flex-col ml-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Filtres</h3>
                <p className="text-xs text-gray-500 mt-0.5">Période et critères d&apos;analyse</p>
              </div>
              <button
                type="button"
                onClick={() => setShowFiltersModal(false)}
                className="w-9 h-9 rounded-lg hover:bg-gray-100 text-gray-500"
                aria-label="Fermer"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Période</p>
                <div className="flex flex-wrap gap-2">
                  {periodOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleDraftPeriodChange(option.value)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        draftPeriod === option.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date début</label>
                  <input
                    type="date"
                    value={format(draftDateRange.start, 'yyyy-MM-dd')}
                    onChange={(e) => {
                      setDraftPeriod('custom');
                      setDraftDateRange((prev) => ({ ...prev, start: new Date(e.target.value) }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date fin</label>
                  <input
                    type="date"
                    value={format(draftDateRange.end, 'yyyy-MM-dd')}
                    onChange={(e) => {
                      setDraftPeriod('custom');
                      setDraftDateRange((prev) => ({ ...prev, end: new Date(e.target.value) }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type de pesée</label>
                <select
                  value={draftFilters.type_pesee}
                  onChange={(e) => setDraftFilters((prev) => ({ ...prev, type_pesee: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous</option>
                  <option value="reception">Réception</option>
                  <option value="transfert">Transfert</option>
                  <option value="expedition">Expédition</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Statut</label>
                <select
                  value={draftFilters.status}
                  onChange={(e) => setDraftFilters((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous</option>
                  <option value="EN_COURS">En cours</option>
                  <option value="EN_ATTENTE_TARE">En attente tare</option>
                  <option value="BRUT_ENREGISTRE">Brut enregistré</option>
                  <option value="TARE_ENREGISTREE">Tare enregistrée</option>
                  <option value="COMPLETEE">Complétée</option>
                  <option value="REFOULEE">Refoulée</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Produit</label>
                <SearchableSelect
                  value={draftFilters.product_id}
                  onChange={(value) => setDraftFilters((prev) => ({ ...prev, product_id: value }))}
                  options={productOptions}
                  placeholder="Tous produits"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
                <SearchableSelect
                  value={draftFilters.client_id}
                  onChange={(value) => setDraftFilters((prev) => ({ ...prev, client_id: value }))}
                  options={clientOptions}
                  placeholder="Tous clients"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fournisseur</label>
                <SearchableSelect
                  value={draftFilters.fournisseur_id}
                  onChange={(value) => setDraftFilters((prev) => ({ ...prev, fournisseur_id: value }))}
                  options={fournisseurOptions}
                  placeholder="Tous fournisseurs"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Magasin</label>
                <SearchableSelect
                  value={draftFilters.magasin_id}
                  onChange={(value) => setDraftFilters((prev) => ({ ...prev, magasin_id: value }))}
                  options={magasinOptions}
                  placeholder="Tous magasins"
                  className="text-sm"
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm"
              >
                Réinitialiser
              </button>
              <button
                type="button"
                onClick={applyFilters}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                Appliquer
              </button>
            </div>
          </aside>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-gray-600">Chargement des statistiques...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-triangle mr-2" />
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {kpiCards.map((card) => (
              <div key={card.label} className={`rounded-xl border p-4 ${card.color}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium opacity-80">{card.label}</p>
                    <p className="mt-1 text-xl font-bold tracking-tight">{card.value}</p>
                  </div>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${card.iconBg}`}>
                    <i className={`${card.icon} text-sm`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {chartIds.map((chartId) => (
              <div key={chartId} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{CHART_TITLES[chartId]}</h3>
                  <button
                    type="button"
                    onClick={() => setExpandedChart(chartId)}
                    className="shrink-0 w-9 h-9 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-blue-600 transition-colors"
                    title="Agrandir le graphique"
                    aria-label="Agrandir le graphique"
                  >
                    <i className="fas fa-expand" />
                  </button>
                </div>
                {renderChart(chartId, 300)}
              </div>
            ))}
          </div>

          {expandedChart && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
              <button
                type="button"
                aria-label="Fermer le graphique"
                className="absolute inset-0 bg-black/50"
                onClick={() => setExpandedChart(null)}
              />
              <div className="relative z-10 w-full max-w-6xl max-h-[92vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{CHART_TITLES[expandedChart]}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {format(dateRange.start, 'dd/MM/yyyy', { locale: fr })} -{' '}
                      {format(dateRange.end, 'dd/MM/yyyy', { locale: fr })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpandedChart(null)}
                    className="w-9 h-9 rounded-lg hover:bg-gray-100 text-gray-500"
                    aria-label="Fermer"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
                <div className="flex-1 min-h-0 p-4 sm:p-6">
                  {renderChart(expandedChart, expandedChartHeight)}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 font-semibold text-gray-900">Top produits</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Produit</th>
                      <th className="px-3 py-2 text-right">Pesées</th>
                      <th className="px-3 py-2 text-right">Poids</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productStats.length === 0 ? (
                      <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400">Aucune donnée</td></tr>
                    ) : productStats.map((row) => (
                      <tr key={row.produit_nom} className="border-t">
                        <td className="px-3 py-2">{row.produit_nom || 'N/A'}</td>
                        <td className="px-3 py-2 text-right">{row.total_pesées}</td>
                        <td className="px-3 py-2 text-right">{formatKg(Number(row.poids_total || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 font-semibold text-gray-900">Top clients</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-right">Pesées</th>
                      <th className="px-3 py-2 text-right">Poids</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientStats.length === 0 ? (
                      <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400">Aucune donnée</td></tr>
                    ) : clientStats.map((row) => (
                      <tr key={row.client_nom} className="border-t">
                        <td className="px-3 py-2">{row.client_nom || 'N/A'}</td>
                        <td className="px-3 py-2 text-right">{row.total_pesées}</td>
                        <td className="px-3 py-2 text-right">{formatKg(Number(row.poids_total || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 font-semibold text-gray-900">Top fournisseurs</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Fournisseur</th>
                      <th className="px-3 py-2 text-right">Pesées</th>
                      <th className="px-3 py-2 text-right">Poids</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierStats.length === 0 ? (
                      <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400">Aucune donnée</td></tr>
                    ) : supplierStats.map((row) => (
                      <tr key={row.fournisseur_nom} className="border-t">
                        <td className="px-3 py-2">{row.fournisseur_nom || 'N/A'}</td>
                        <td className="px-3 py-2 text-right">{row.total_pesées}</td>
                        <td className="px-3 py-2 text-right">{formatKg(Number(row.poids_total || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsTab;
